'use client';

import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import type {
  FormData,
  FormErrors,
  OnChangeFormInput,
  UseFormReturn,
} from 'src/hooks';
import type CardClient from 'src/model/CardClient';
import type { Cart } from 'src/model/Cart';
import type { DeliveryOptions } from 'src/model/DeliveryOptions';
import PaymentType from 'src/model/PaymentType';
import type Client from 'src/model/User';
import type { Summary } from '..';
import { AuthWrapper } from '../../../../../features/core/auth/screen/AuthWrapper';
import type { Address } from '../../../../../model/Address';
import { BillingAddressScreen } from '../components/BillingAddress/';
import { OrderSummary } from '../components/OrderSummary';
import { PaymentFormUI } from '../components/PaymentForm';
import { ProductListUI } from '../components/ProductList/ui';
import type { CartView } from '../../Cart';
import { useAddressForm } from '../hooks/use-address-form';
import type { CorreiosCode } from 'src/model/CorreiosCode';
import { toast } from 'react-toastify';
import { trackTikTokEvent } from 'src/pixel/tiktok';

interface CheckoutUIProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  addresses: Address[] | undefined;
  selectedAddress?: string;
  carts: Cart[];
  cartView: CartView[];
  user: Client;
  selectedCard?: number;
  addNewCard: boolean;
  deliveryOptions?: DeliveryOptions[];
  summary: Summary;
  selectedCreditCard: boolean;
  selectedPix: boolean;
  selectedBoleto: boolean;
  discount: number;
  totalAfterDiscount?: number;
  formDataCoupon: FormData;
  formDataCouponErrors: FormErrors;
  onAddCard: () => void;
  onSelectCard: (index: number, cardSelected: CardClient) => void;
  onSubmitRegister: (e: React.FormEvent) => void;
  onSelectAddress: (id: string) => void;
  checkoutBlocked: boolean;
  onPayment: () => void;
  onApplyCoupon: () => void;
  onSelectType: (type: PaymentType) => void;
  onChangeFormCouponInput: OnChangeFormInput;
  shippingCost?: number;
  selectedDeliveryType: CorreiosCode;
  onSelectDeliveryType: (type: CorreiosCode) => void;
  selectedInstallments: number;
  onSelectInstallments: (n: number) => void;
  voucherAvailable: boolean;
  voucherMessage?: string;
}

export const CheckoutUI: React.FC<CheckoutUIProps> = ({
  formData,
  formErrors,
  carts,
  user,
  addresses,
  selectedAddress,
  selectedCard,
  addNewCard,
  summary,
  selectedCreditCard,
  selectedPix,
  selectedBoleto,
  discount,
  totalAfterDiscount,
  formDataCoupon,
  formDataCouponErrors,
  onAddCard,
  onSelectCard,
  onSubmitRegister,
  onSelectAddress,
  checkoutBlocked,
  onChangeFormInput,
  onPayment,
  onApplyCoupon,
  onSelectType,
  onChangeFormCouponInput,
  shippingCost,
  selectedDeliveryType,
  onSelectDeliveryType,
  selectedInstallments,
  onSelectInstallments,
  voucherAvailable,
  voucherMessage,
}) => {
  const {
    selectedAddressId,
    showNewAddressForm,
    newAddressData,
    newAddressErrors,
    handleAddressSelection,
    handleNewAddressInput,
    handleCEPChange,
  } = useAddressForm(addresses || []);

  type CheckoutStep = 1 | 2 | 3;
  const steps: CheckoutStep[] = [1, 2, 3];
  const stepLabels = ['Produtos & Endereço', 'Pagamento', 'Resumo'];
  const stepIcons = [
    <svg
      key="products"
      className="w-4 h-4 sm:w-5 sm:h-5"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 2L3 7v11a1 1 0 001 1h3a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V7l-7-5z"
        clipRule="evenodd"
      />
    </svg>,
    <svg
      key="payment"
      className="w-4 h-4 sm:w-5 sm:h-5"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
      <path
        fillRule="evenodd"
        d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM9 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
        clipRule="evenodd"
      />
    </svg>,
    <svg
      key="summary"
      className="w-4 h-4 sm:w-5 sm:h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>,
  ];
  const [step, setStep] = useState<CheckoutStep>(1);
  const [selectedInstallmentsFromPayment, setSelectedInstallmentsFromPayment] =
    useState(1);
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    PaymentType | undefined
  >(undefined);
  const initiateCheckoutFiredRef = useRef(false);
  console.log(summary);
  const selectedBuyerAddress = addresses?.find(
    (addr) => addr.id === selectedAddress
  );
  const buyerCep = selectedBuyerAddress?.zipcode ?? '';
  const sellerCep = carts[0].products.storeSeller.cep ?? '';

  useEffect(() => {
    if (!initiateCheckoutFiredRef.current && carts && carts.length > 0) {
      const contentIds = carts.map((cart) => cart.products.id);

      const contents = carts.map((cart) => ({
        id: cart.products.id,
        quantity: cart.cartProduct.quantity,
        item_price: cart.cartProduct.grade.price,
      }));

      const totalValue = contents.reduce((sum, item) => {
        return sum + item.item_price * item.quantity;
      }, 0);

      trackTikTokEvent('InitiateCheckout', {
        content_ids: contentIds,
        contents: contents,
        value: Number(totalValue).toFixed(2),
        currency: 'BRL',
        num_items: carts.length,
      });

      initiateCheckoutFiredRef.current = true;
    }
  }, []);

  const getInterestRate = (installments: number): number => {
    const rates: Record<number, number> = {
      1: 0,
      2: 0,
      3: 3.49,
      4: 3.99,
      5: 4.49,
      6: 4.99,
    };
    return rates[installments] || 0;
  };

  const calculateFinalDisplayTotal = (): number => {
    if (
      typeof totalAfterDiscount === 'number' &&
      Number.isFinite(totalAfterDiscount)
    ) {
      return totalAfterDiscount;
    }

    return summary.total ?? 0;
  };

  const finalDisplayTotalForSummary = calculateFinalDisplayTotal();

  const validateStep = (): boolean => {
    if (step === 1) {
      return selectedAddress !== undefined && selectedAddress !== '';
    }
    if (step === 2) {
      if (
        selectedPix ||
        selectedBoleto ||
        (selectedPaymentType !== undefined && !selectedCreditCard)
      )
        return true;
      return selectedCard !== undefined && selectedCard >= 0;
    }
    if (step === 3) {
      return true;
    }
    return false;
  };

  const handleNextStep = () => {
    if (
      step === 2 &&
      selectedPaymentType === PaymentType.VOUCHER &&
      !voucherAvailable
    ) {
      toast.error(voucherMessage || 'Voucher indisponível para este carrinho.');
      return;
    }
    if (validateStep()) {
      setStep((prev) => Math.min(prev + 1, steps.length) as CheckoutStep);
    } else {
      alert('Preencha os campos obrigatórios antes de continuar.');
    }
  };

  const handlePrevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1) as CheckoutStep);
  };

  const handleReturnToShopping = () => {
    window.location.href = '/';
  };

  const Stepper: React.FC<{ currentStep: number }> = ({ currentStep }) => {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-6 mb-4 sm:mb-8">
        <div className="flex items-center justify-between sm:justify-center">
          {stepLabels.map((label, index) => {
            const stepIndex = index + 1;
            const isActive = currentStep === stepIndex;
            const isCompleted = currentStep > stepIndex;

            return (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center min-w-0 flex-1 sm:min-w-[120px] sm:flex-initial">
                  <div
                    className={`flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 ${
                      isCompleted
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                        : isActive
                          ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg scale-105 sm:scale-110'
                          : 'bg-gray-100 text-gray-400 border border-gray-200 sm:border-2'
                    }`}
                  >
                    {isCompleted ? (
                      <svg
                        className="w-4 h-4 sm:w-6 sm:h-6"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      stepIcons[index]
                    )}
                  </div>
                  <span
                    className={`mt-1 sm:mt-3 text-xs sm:text-sm font-semibold text-center transition-all duration-300 leading-tight ${
                      isActive
                        ? 'text-red-600'
                        : isCompleted
                          ? 'text-green-600'
                          : 'text-gray-400'
                    }`}
                  >
                    <span className="hidden sm:inline">{label}</span>
                    <span className="sm:hidden">
                      {label.split(' ')[0]}
                      {label.includes('&') && <br />}
                      {label.split(' & ')[1]}
                    </span>
                  </span>
                </div>

                {index < stepLabels.length - 1 && (
                  <div className="flex-grow mx-1 sm:mx-4 max-w-[40px] sm:max-w-none">
                    <div className="relative h-0.5 sm:h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`absolute top-0 left-0 h-full transition-all duration-500 ${
                          currentStep > stepIndex
                            ? 'bg-gradient-to-r from-green-500 to-green-600 w-full'
                            : 'bg-gray-200 w-0'
                        }`}
                      ></div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <AuthWrapper>
      <div className="w-full sm:container sm:mx-auto sm:px-6 bg-gradient-to-br from-gray-50 to-gray-100 py-4 sm:py-0">
        <div
          className={`mx-auto px-2 sm:px-4 ${step === 3 ? 'w-full max-w-none' : 'container max-w-7xl'}`}
        >
          <div className="text-center mb-4 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl font-black text-gray-900 mb-2 sm:mb-3">
              Finalizar Compra
            </h1>
            <p className="text-sm sm:text-lg text-gray-600">
              Etapa {step} de {steps.length} -{' '}
              <span className="hidden sm:inline">{stepLabels[step - 1]}</span>
              <span className="sm:hidden">
                {stepLabels[step - 1].split(' ')[0]}
              </span>
            </p>
          </div>
          <Stepper currentStep={step} />

          <div className="mx-auto">
            {step === 1 && (
              <div className="space-y-4 sm:space-y-8">
                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-8">
                  <div className="order-1 lg:order-1">
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                        <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
                        Seus Produtos
                      </h2>
                      {carts && carts.length > 0 && (
                        <ProductListUI
                          cartsProducts={carts}
                          totalAfterDiscount={totalAfterDiscount}
                        />
                      )}
                    </div>
                  </div>

                  <div className="order-2 lg:order-2">
                    <BillingAddressScreen
                      addresses={addresses}
                      onSelectAddress={onSelectAddress}
                      selectedAddress={selectedAddress}
                    />
                  </div>
                </div>

                <div className="flex justify-center sm:justify-end pt-4 sm:pt-6">
                  <button
                    onClick={handleNextStep}
                    disabled={checkoutBlocked}
                    className={`w-full sm:w-auto font-bold py-3 sm:py-4 px-6 sm:px-10 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base
                      ${
                        checkoutBlocked
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none hover:scale-100'
                          : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                      }`}
                  >
                    Continuar para Pagamento
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 sm:space-y-8">
                {user && (
                  <PaymentFormUI
                    addNewCard={addNewCard}
                    formData={formData}
                    formErrors={formErrors}
                    onAddCard={onAddCard}
                    onChangeFormInput={onChangeFormInput}
                    card={user.cards}
                    selectedCard={selectedCard}
                    selectedPix={selectedPix}
                    selectedBoleto={selectedBoleto}
                    onSelectCard={onSelectCard}
                    onSubmitRegister={onSubmitRegister}
                    onSelectType={(type) => {
                      onSelectType(type);
                      setSelectedPaymentType(type);
                    }}
                    selectedCreditCard={selectedCreditCard}
                    totalAmount={summary.total}
                    shippingCost={shippingCost || 0}
                    addresses={addresses || []}
                    selectedAddressId={selectedAddressId}
                    onAddressSelection={handleAddressSelection}
                    showNewAddressForm={showNewAddressForm}
                    newAddressData={newAddressData}
                    newAddressErrors={newAddressErrors}
                    onChangeNewAddressInput={handleNewAddressInput}
                    onChangeCEP={handleCEPChange}
                    selectedInstallments={selectedInstallments}
                    onInstallmentChange={onSelectInstallments}
                    voucherAvailable={voucherAvailable}
                    voucherMessage={voucherMessage}
                  />
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 pt-4 sm:pt-6">
                  <button
                    onClick={handlePrevStep}
                    className="order-2 sm:order-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 sm:px-8 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={handleNextStep}
                    className="order-1 sm:order-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 sm:py-4 px-6 sm:px-10 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base"
                  >
                    Continuar para Resumo
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4 sm:space-y-8">
                <OrderSummary
                  summary={summary}
                  onContinueToPayment={onPayment}
                  onReturnToShopping={handleReturnToShopping}
                  discount={discount}
                  fetchCouponDiscount={onApplyCoupon}
                  totalAfterDiscount={finalDisplayTotalForSummary}
                  onChangeFormInput={onChangeFormCouponInput}
                  formData={formDataCoupon}
                  formErrors={formDataCouponErrors}
                  shippingCost={shippingCost || 0}
                  selectedDeliveryType={selectedDeliveryType}
                  onSelectDeliveryType={onSelectDeliveryType}
                  buyerCep={buyerCep}
                  sellerCep={sellerCep}
                />

                <div className="flex justify-center sm:justify-start pt-4 sm:pt-6">
                  <button
                    onClick={handlePrevStep}
                    className="w-full sm:w-auto bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 sm:px-8 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    Voltar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthWrapper>
  );
};
